import i18n from "@/utils/i18n";
// Mirrors backend DTOs in euas-backend/src/modules/educations/dto/response/.
// Keep permissive (optional fields) — backend may omit.

export enum EducationType {
  ON_SITE = 0,
  ONLINE = 1,
  UNIT = 2,
  NONUNIT = 3,
}

export enum ObjectiveType {
  INTERNAL_FILE = 0,
  LINK = 1,
  JITSI = 2,
}

export enum ObjectiveCompletionStatus {
  NOT_STARTED = 0,
  IN_PROGRESS = 1,
  COMPLETED = 2,
}

export enum EnrollmentStatus {
  REQUESTED = 0,
  APPROVED_BRANCH_MANAGER = 1,
  ENROLLED = 2,
  REJECTED_BY_BRANCH_MANAGER = 3,
  REJECTED_BY_CENTRAL_MANAGER = 4,
  CANCELLED = 5,
  CONTENT_COMPLETED = 6,
  EXAM_FAILED = 7,
  EXAM_COMPLETED = 8,
  COMPLETED = 9,
  /**
   * Yönetici eğitimi kapatırken bu kullanıcıyı "tamamlamadı" olarak işaretledi
   * (genelde devamsızlık nedeniyle). Kullanıcı geçmişte kaldığı eğitim olarak
   * görür; ilerleyemez.
   */
  NOT_COMPLETED = 11,
  EXAM_STARTED = 65,
  SURVEY_STARTED = 82,
}

export interface ScheduledTime {
  date?: string;       // YYYY-MM-DD
  startTime?: string;  // HH:mm
  endTime?: string;    // HH:mm
}

export interface InstructorInfo {
  id?: string;
  firstName?: string;
  lastName?: string;
  name?: string;
}

export interface CategoryInfo {
  id?: string;
  name?: string;
  parentName?: string | null;
}

export interface DocumentFileInfo {
  id: string;
  originalName?: string;
  downloadUrl?: string | null;
  size?: number;
}

export interface FileInfo {
  id: string;
  originalName?: string;
  mimeType?: string;
  downloadUrl?: string | null;
  durationMinutes?: number | null;
}

export interface ObjectiveVideoQuestionOption {
  id: string;
  optionText?: string;
  text?: string;
  order?: number;
  isCorrect?: boolean;
}

export interface ObjectiveVideoQuestion {
  id: string;
  questionText?: string;
  question?: string;
  timestamp?: string;
  triggerSeconds?: number;
  order?: number;
  options?: ObjectiveVideoQuestionOption[];
  correctOptionText?: string;
}

export interface ObjectiveDto {
  id: string;
  chapterID: string;
  type: ObjectiveType;
  title: string;
  fileID?: string | null;
  externalUrl?: string | null;
  token?: string | null;
  order?: number | null;
  status?: ObjectiveCompletionStatus;
  completionStatus?: ObjectiveCompletionStatus;
  durationMinutes?: number | null;
  file?: FileInfo | null;
  videoQuestions?: ObjectiveVideoQuestion[];
  watchedSeconds?: number;
  lastPositionSeconds?: number;
  progressPercent?: number;
}

export interface ChapterDto {
  id: string;
  educationID: string;
  title: string;
  order?: number | null;
  durationHours?: number | null;
  durationMinutes?: number | null;
  progressPercentage?: number | null;
  objectives: ObjectiveDto[];
}

export interface VirtualClassroomDto {
  id: string;
  educationID: string;
  name?: string | null;
  jitsiRoomName?: string | null;
  scheduledTimes?: ScheduledTime[] | null;
  location?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  educationFacility?: { name?: string; room?: string; locationName?: string } | null;
  myParticipationMinutes?: number | null;
}

/** Mazeret Nedeni enum'u — BE ile birebir (Madde 10). */
export enum ExcuseReason {
  HEALTH = 0,
  ANNUAL_LEAVE = 1,
  BUSINESS_TRAVEL = 2,
  OFFICIAL_DUTY = 3,
  FAMILY_EMERGENCY = 4,
  OTHER = 5,
}

export const EXCUSE_REASON_LABELS: Record<ExcuseReason, string> = {
  [ExcuseReason.HEALTH]: i18n.t("excuseReasons.health"),
  [ExcuseReason.ANNUAL_LEAVE]: i18n.t("excuseReasons.annualLeave"),
  [ExcuseReason.BUSINESS_TRAVEL]: i18n.t("excuseReasons.businessTravel"),
  [ExcuseReason.OFFICIAL_DUTY]: i18n.t("excuseReasons.officialDuty"),
  [ExcuseReason.FAMILY_EMERGENCY]: "Ailevi Mazeret",
  [ExcuseReason.OTHER]: i18n.t("excuseReasons.other"),
};

export interface AttendanceRecord {
  attendanceDate: string;
  status: 0 | 1;
  /** Madde 10: status = 0 (Katılmadı) ise dolu olur. */
  excuseReason?: ExcuseReason | null;
}

export interface EnrollmentDto {
  id: string;
  educationID: string;
  userID: string;
  status: EnrollmentStatus;
  progressPercent: number;
  requestedAt?: string;
  reviewedAt?: string | null;
  lastActivityAt?: string | null;
  completedAt?: string | null;
  surveyCompletedAt?: string | null;
  attendances?: AttendanceRecord[];
  examAttempts?: ExamAttemptDto[];
  user?: {
    id?: string;
    firstName?: string;
    lastName?: string;
  };
}

export interface EducationCommentUserDto {
  id: string;
  firstName?: string;
  lastName?: string;
}

export interface EducationCommentDto {
  id: string;
  content: string;
  createdAt: string;
  user: EducationCommentUserDto;
}

export interface EducationCommentsResponseDto {
  commentsEnabled: boolean;
  items: EducationCommentDto[];
}

export interface EducationDetailDto {
  id: string;
  code?: string | null;
  name: string;
  description?: string | null;
  content?: string | null;
  type: EducationType;
  status?: number;
  isRequired?: boolean;
  capacity?: number;
  durationHours?: number;
  durationMinutes?: number;
  startDate?: string | null;
  endDate?: string | null;
  requestCollectionStartDate?: string | null;
  requestCollectionEndDate?: string | null;
  isAcceptingRequestCollection?: boolean;
  scheduledTimes?: ScheduledTime[] | null;
  location?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  transportation?: string | null;
  coverImageFileID?: string | null;
  coverImageUrl?: string | null;
  categories?: CategoryInfo[];
  instructors?: InstructorInfo[];
  documentFiles?: DocumentFileInfo[];
  exam?: { id: string; name?: string; passingScore?: number; durationMinutes?: number } | null;
  survey?: string | null;
  certificateId?: string | null;
  chapters?: ChapterDto[];
  virtualClassrooms?: VirtualClassroomDto[];
  enrollment?: EnrollmentDto | null;
}

export interface ExamAttemptDto {
  id: string;
  enrollmentID: string;
  startedAt?: string;
  endsAt?: string | null;
  finishedAt?: string | null;
  status: 0 | 1 | 2;
  scorePercentage?: number | null;
}

export interface CertificateDto {
  id: string;
  certificateNumber?: string;
  issuedAt?: string;
  downloadUrl?: string;
  previewUrl?: string;
}
